import {Handle, Node, Position, useEdges, useReactFlow} from '@xyflow/react';
import {NodeEditDialog, NodeEditDialogProps} from '../../node-edit-dialog';
import {useMemo, useEffect, useState} from 'react';
import {
  PopoverContextMenu,
  UsePopoverContextMenuReturn,
} from '../../../components/popover-context-menu';
import {BaseNode} from '../BaseNode';
import {NodeFeatures} from '../types';
import {NodeCreateDialog, NodeCreateDialogProps} from '../../node-create-dialog';
import TaskForm from '@/pages/resources/tasks/task-form';
import useWorkflowStore from '../../store';
import formatter from '@/lib/formatter';
import {cn} from '@/lib/tailwind-utils';
import {useNavigate} from 'react-router';
import {ActivityIcon, PinIcon} from 'lucide-react';
import {TaskNodeView} from './task-node-view';
import {Button} from '@/shadcn_components/ui/button';
import {getPriorityInfo, getStatusInfo} from '@/pages/resources/tasks/types';

export const TaskNode = (props: {data: any; selected?: boolean}) => {
  const {data = {}, selected} = props;
  const edges = useEdges();
  const {setEdges} = useReactFlow(); // Hàm cập nhật edges
  const id = data.uuid;
  const statusInfo = getStatusInfo(data?.status, data);
  const handlePositions = [
    {id: 'source-right', position: Position.Right},
    {id: 'source-top', position: Position.Top},
    {id: 'source-bottom', position: Position.Bottom},
  ];

  const handleStyle = {
    width: 12,
    height: 12,
    backgroundColor: 'white',
    border: '1px solid black',
  };

  useEffect(() => {
    const uniqueEdges = new Map();

    // Duyệt qua edges và chỉ giữ lại edge mới nhất
    edges.forEach(edge => {
      const key = `${edge.source}-${edge.target}`;
      if (uniqueEdges.has(key)) {
        // Nếu đã tồn tại, xóa edge cũ
        setEdges(prevEdges => prevEdges.filter(e => e !== uniqueEdges.get(key)));
      }
      // Lưu lại edge mới nhất
      uniqueEdges.set(key, edge);
    });
  }, [edges, setEdges]);

  const bgColor = useMemo(() => {
    return statusInfo.bgcolor;
  }, [statusInfo.bgcolor, data]);

  return (
    <BaseNode selected={selected} className={cn(`${bgColor}`)}>
      <div className={cn('flex flex-row items-center justify-between', bgColor)}>
        {!data.name ? (
          <p className="self-center flex-grow text-red-600">{'<Không tồn tại>'}</p>
        ) : (
          <TaskNodeView data={data} />
        )}
      </div>

      {/* Handle cho ngõ vào */}
      <Handle
        type="target"
        position={Position.Left}
        id="target-left"
        style={{width: 12, height: 12, backgroundColor: 'black'}}
      />
      {handlePositions.map(handle => (
        <Handle
          key={handle.id}
          type="source"
          position={handle.position}
          id={handle.id}
          style={handleStyle}
        />
      ))}
    </BaseNode>
  );
};

const EditDialog = (props: NodeEditDialogProps) => {
  const {dialogState} = props;
  const {id: node_id, data} = dialogState?.extraData || {};
  const recalculateTaskNodeDates = useWorkflowStore(state => state.recalculateTaskNodeDates);

  return (
    <NodeEditDialog
      title={`Công việc ${data?.name}`}
      transformBeforeSave={TaskForm.transformBeforeSaveInWorkflow}
      onSuccess={node => {
        recalculateTaskNodeDates();
      }}
      {...props}
      // onSubmit={onSubmit}
    >
      <TaskForm.GeneralInfo />
    </NodeEditDialog>
  );
};

const CreateDialog = (props: NodeCreateDialogProps) => {
  const project_id = useWorkflowStore(state => state.project_id);

  return (
    <NodeCreateDialog
      title={`Tạo công việc`}
      formProps={{defaultValues: {...TaskForm.defaultValues, project_id}}}
      transformBeforeSave={TaskForm.transformBeforeSaveInWorkflow}
      {...props}
      // onSubmit={onSubmit}
    >
      <TaskForm.GeneralInfo />
    </NodeCreateDialog>
  );
};

const ContextMenu = ({
  popoverContextMenu,
  node,
}: {
  popoverContextMenu: UsePopoverContextMenuReturn;
  node: Node<any>;
}) => {
  const {dialogState} = useWorkflowStore();
  const navigate = useNavigate();

  const contextMenuItems = useMemo(
    () => [
      {
        id: 'view-task',
        label: 'Xem chi tiết',
        onClick(m) {
          navigate(`/tasks/${node.data.id}/show`);
        },
      },
      {
        id: 'edit-task',
        label: 'Sửa công việc',
        onClick(m) {
          dialogState.editNode?.openDialog(node);
        },
      },
      {
        id: 'view-workflow',
        label: 'Sửa workflow',
        onClick(m) {
          navigate(`/tasks/${node.data.id}/editwf`);
        },
      },
    ],
    [],
    // [position],
  );

  return <PopoverContextMenu title="" menuItems={contextMenuItems} {...popoverContextMenu} />;
};

export const taskNodeFeatures: NodeFeatures = {
  CreateDialog,
  EditDialog,
  ContextMenu,
};
